import React, { useEffect, useRef, useState } from "react"
import { HeartIcon, PlayCircleIcon, InformationCircleIcon, TrashIcon, StarIcon as StarOutline } from "@heroicons/react/24/outline"
import { HeartIcon as HeartSolid, StarIcon, CheckBadgeIcon } from "@heroicons/react/24/solid"
import useClientAuth from "../hooks/useClientAuth"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import { navigate } from "vike/client/router"
import Img from "../ui/Img"
import Chip from "../ui/Chip"
import ShopButton from "../ui/ShopButton"
import TrailerButton from "../ui/TrailerButton"
import PlaylistPreviewButton from "../ui/PlaylistPreviewButton"
import { ProductBreadcrumb } from "../ui/Categories/CategoryBreadcrumb"
import ProductSharePanel from "../ui/ProductSharePanel"
import OrderTermsNote from "../ui/OrderTermsNote"
import MoreByAuthor from "../ui/MoreByAuthor"
import ProductRatingSummary from "../ui/ProductRatingSummary"
import AboutAuthor from "../ui/AboutAuthor"
import RecentlyViewed from "../ui/RecentlyViewed"
import ProductReviews from "../ui/Reviews/ProductReviews"
import LeaveReview, { ReviewSuccessfullyAdded } from "../ui/Reviews/LeaveReview"
import SeeAlsoHorizontal from "../ui/SeeAlsoHorizontal"
import useT, { useCurrencyFormat } from "../hooks/useT"
import { useCategories, useProfileName } from "../hooks/useCatalogData"
import { analyzeRatings } from "../utils/ratings"
import { formatDate, formatNumber, isPhysicalProduct, isSoldInCoins } from "../utils/productUtils"
import { activeDiscountPercent, applyDiscount } from "../utils/discountUtils"
import { authorUrl, buildContentUrl } from "../utils/contentRoutes"
import { getImageSize } from "../utils/images"
import { addToCart, removeFromCart, useCart, cartLineOf } from "../store/cart"
import { lineKey } from "../checkout/cartItems"
import useSingleCopy from "../hooks/useSingleCopy"
import { useFavorite, useStoreAccount, ownsProduct } from "../store/account"
import { openPlayer } from "../player"
import { requestSignIn } from "../utils/signIn"

// the page gutters, as on the store product page:
const PAGE_PX = "px-4 sm:px-10 lg:px-[60px]"
// the hero keeps its own, wider gutters: the cover lines up with the buy button
const HERO_PX = "px-4 sm:px-[50px] md:px-[80px] lg:px-[160px] xl:px-[280px]"

// Cover image that slowly pans when taller than its container
function PanningCover({ image, alt }) {
  const ref = useRef(null)
  const [offset, setOffset] = useState(0)
  const [natural, setNatural] = useState({ width: 0, height: 0 })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const recalc = () => {
      // a plain url carries no size: the loaded <img> gives the aspect ratio
      // (read off the element too — an SSR image may load before hydration)
      const img = el.querySelector("img")
      const loaded = img?.naturalWidth ? { width: img.naturalWidth, height: img.naturalHeight } : natural
      const size = getImageSize(image).width ? getImageSize(image) : loaded
      if (!size.width) return
      const adjustedHeight = (size.height * el.clientWidth) / size.width
      setOffset(Math.max(0, adjustedHeight - el.clientHeight))
    }
    recalc()
    const observer = new ResizeObserver(recalc)
    observer.observe(el)
    return () => observer.disconnect()
  }, [image, natural])
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden -z-10">
      <Img
        src={image}
        alt={alt}
        onLoad={(e) => setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
        loading="eager"
        fetchPriority="high"
        sizes="100vw"
        className="w-full h-[300px] sm:h-[400px] md:h-auto"
        imgClassName="h-[300px] sm:h-[400px] md:h-auto object-cover md:object-top"
        style={offset > 0 ? { "--sf-pan-offset": `-${offset}px`, animation: `sf-cover-pan ${offset / 5}s linear infinite alternate` } : undefined}
      />
    </div>
  )
}

function CreditsBox({ label, name, link, thumbnail }) {
  return (
    <div className="flex flex-row md:flex-col items-end md:items-start justify-start md:justify-between gap-4 md:gap-0">
      <span className="text-[14px] text-text_secondary uppercase tracking-wide">{label}</span>
      <div className="flex flex-col gap-2 items-end md:items-start">
        {thumbnail && link && (
          <Button variant="none" href={link} className="w-[60px] h-[60px] rounded-full overflow-hidden hover:opacity-80">
            <Img src={thumbnail} alt={name} width={60} height={60} className="w-full h-full" />
          </Button>
        )}
        {link ? (
          <Button variant="link" href={link} className="text-[16px] font-semibold">
            {name}
          </Button>
        ) : (
          <span className="text-[16px] font-semibold">{name}</span>
        )}
      </div>
    </div>
  )
}

function ProductDescription({ product }) {
  const contentTypeLabel = useProfileName(product?.profile)
  const t = useT()
  const { list: categories } = useCategories()
  const roles = [
    { id: "author", label: t`Author` },
    { id: "illustrator", label: t`Illustrator` },
    { id: "translator", label: t`Translator` },
    { id: "narrator", label: t`Narrator` },
  ]
  return (
    <div className="flex flex-col md:flex-row justify-between gap-6 mb-[60px] flex-1">
      <div className="flex flex-col justify-start gap-4 md:gap-6 mb-10 md:mb-0">
        {roles
          .filter(({ id }) => product[id]?.length > 0)
          .flatMap(({ id, label }) => {
            const items = product[id]
            if (id === "author" && items.length > 1) {
              return items.map((author, i) => (
                <CreditsBox key={`${id}-${i}`} label={i === 0 ? label : ""} name={author.name} link={authorUrl(author)} thumbnail={author.thumbnail?.default ? author.thumbnail : null} />
              ))
            }
            const single = id === "author" && items.length === 1 ? items[0] : null
            return <CreditsBox key={id} label={label} name={items.map((x) => x.name).join(" | ")} link={single ? authorUrl(single) : null} thumbnail={single?.thumbnail?.default ? single.thumbnail : null} />
          })}
      </div>
      <div className="w-full md:w-[70%] md:max-w-[670px] flex flex-col">
        <p className="m-0 mb-[50px] whitespace-pre-line text-justify text-[16px] leading-relaxed">{product.description}</p>
        {/* the same note as under the stats: a digital title is read or played here, never a file */}
        {!isPhysicalProduct(product) && (
          <div className="flex flex-row items-start gap-2 -mt-[20px] pt-[30px] mb-[30px] border-t border-bordered" data-testid="digital-disclaimer-description">
            <InformationCircleIcon className="w-5 h-5 shrink-0 text-text_secondary" aria-hidden="true" />
            <Typography variant="txt1_dim" className="block mt-0.5">
              {t`digital_title_library_note`}
            </Typography>
          </div>
        )}
        <ProductSharePanel product={product} className="mb-4" />
        <OrderTermsNote product={product} className="mb-10" />
        <div className="flex flex-row flex-wrap gap-2.5" data-testid="product-categories">
          {contentTypeLabel && <Chip label={contentTypeLabel} to={`/type/${product.profile?.id}`} data-testid="product-type-chip" />}
          {product.category?.map((id) => {
            const category = categories.find((c) => c.categoryId === id)
            if (!category) return null
            return <Chip key={id} label={category.name} to={`/category/${category.categoryId}`} />
          })}
        </div>
      </div>
    </div>
  )
}

function Property({ label, value }) {
  return (
    <div className="flex flex-col items-center lg:items-start justify-center w-1/2 lg:w-auto h-[125px] lg:h-auto border-[5px] lg:border-0 border-white">
      <Typography 
        variant="label_upper_dim" 
        className="text-center lg:text-left"
      >
        {label}
      </Typography>
      <Typography variant="txt_accent" className="text-center lg:text-left">
        {value}
      </Typography>
    </div>
  )
}

function ProductProperties({ product }) {
  const t = useT()
  return (
    <div className="flex flex-row flex-wrap items-center justify-between w-full min-h-[125px] bg-gradient-to-br from-primary_lt/50 to-accent/[0.06] rounded-xl py-0 lg:py-[38px] px-0 lg:px-5 xl:px-[60px] mb-[88px]">
      <Property
        label={t`Published by`}
        value={
          <span className="inline-flex items-center gap-1.5">
            {product.publisherSlug ? (
              <a href={`/seller/${product.publisherSlug}`} className="text-primary hover:underline">{t(product.publisher || "")}</a>
            ) : (
              <span className="text-primary">
                {t(product.publisher || "")}
              </span>
            )}
            {/* a person read this company's application and said yes */}
            {product.publisherVerified && (
              <Tooltip text={t`Verified merchant`}>
                <CheckBadgeIcon className="w-4 h-4 text-primary" data-testid="publisher-verified" />
              </Tooltip>
            )}
          </span>
        }
      />
      <Property label={t`Publish date`} value={formatDate(product.created)} />
      <Property label={t`Language`} value={product.languages || t`Bulgarian`} />
      <Property label={t`Duration`} value={t("%duration% minutes", product.duration ?? "-")} />
    </div>
  )
}

// Buy / Rent (credits) or Add to Cart (real money) or Free / Get
function PurchaseButtons({ product, account, onFreeGet }) {
  const t = useT()
  // Server-rendered buttons stay disabled until React is attached so an early
  // click is not lost before hydration
  const [ready, setReady] = useState(false)
  useEffect(() => setReady(true), [])
  const { localizedCost } = useCurrencyFormat()
  const discountPercent = activeDiscountPercent(product)
  const realPrice = (cost) => localizedCost(applyDiscount(parseInt(cost) || 0, discountPercent), product.real_money_currency, 100)
  const gotoCredits = () => (account ? navigate(`/buy-coins?pid=${encodeURIComponent(product.pid)}`) : requestSignIn())
  // had once (what plays here): in the cart already, the button takes it out —
  // there is no second copy to add
  const single = useSingleCopy(product)
  const inCart = cartLineOf(useCart(), product.pid)

  // As on the old site, the buttons sit over the hero on md+ (absolute,
  // anchored to the stats panel); a play-icon sized placeholder keeps the
  // panel layout identical to the owned state.
  const anchored = "flex flex-col items-center md:items-start gap-4 md:absolute md:left-[80px] lg:left-[160px] xl:left-[280px]"
  // on a phone the buttons sit on the dark hero: outlined in white there, the
  // dark pill from md, where they sit over the picture
  const outlined = "border-2 border-white bg-transparent text-white hover:bg-white hover:text-dark md:border-0 md:bg-dark md:text-white md:hover:bg-black md:hover:text-white"
  if (product.free_access) {
    return (
      <div className="flex flex-col md:flex-row mx-auto md:mx-0">
        <div className="hidden md:block w-[80px] lg:w-[100px] h-[80px] lg:h-[100px]" />
        <div className={`${anchored} md:bottom-[190px] lg:bottom-[210px] mt-[30px] md:mt-0`}>
          <ShopButton className={outlined} cost={t`Free`} label={t`Get`} showCredits={false} onClick={onFreeGet} disabled={!ready} />
        </div>
      </div>
    )
  }
  // what the product is sold at is its primary price model, not whether coins
  // are accepted — every migrated product accepts them
  const realMoneyOnly = !isSoldInCoins(product)
  // the price it is sold at: in money, or in credits — a product sold only for
  // money has no credit price (`cost` 0), and must still offer its button
  const buyable = realMoneyOnly ? !!product.real_money_cost : !!product.cost
  return (
    <div className="flex flex-col md:flex-row mx-auto md:mx-0">
    <div className="hidden md:block w-[80px] lg:w-[100px] h-[80px] lg:h-[100px]" />
    <div className={`${anchored} md:bottom-[200px] lg:bottom-[220px]`}>
      <div className="flex flex-col md:flex-row items-center gap-5">
        {buyable && realMoneyOnly && single && inCart ? (
          <Button variant="primary_l" onClick={() => removeFromCart(lineKey(inCart))} data-testid="remove-from-cart-button">
            <TrashIcon className="w-6 h-6" />
            {t`Remove from cart`}
          </Button>
        ) : buyable && (
          <ShopButton
            // the real-money add-to-cart is the brand orange on every width
            className={realMoneyOnly ? "" : outlined}
            cartVariant="primary_l"
            cost={applyDiscount(product.cost, discountPercent)}
            real_price={realPrice(product.real_money_cost)}
            struck_price={discountPercent > 0 ? (realMoneyOnly ? localizedCost(parseInt(product.real_money_cost) || 0, product.real_money_currency, 100) : t("%cost% credits", product.cost)) : null}
            label={realMoneyOnly ? t`Add to Cart` : t`Buy`}
            showCredits={!realMoneyOnly}
            showCartIcon={realMoneyOnly}
            disabled={!ready}
            onClick={() => (realMoneyOnly ? addToCart(product, 1, {}, { placement: "media_page", proceedToCart: true }) : gotoCredits())}
          />
        )}
        {!!product.rent_cost && !realMoneyOnly && (
          <ShopButton
            className={outlined}
            cost={applyDiscount(product.rent_cost, discountPercent)}
            real_price={realPrice(Math.round(product.real_money_cost / (product.cost / product.rent_cost)))}
            struck_price={discountPercent > 0 ? t("%cost% credits", product.rent_cost) : null}
            label={product.expired ? t`Renew` : t`Rent`}
            disabled={!ready}
            onClick={gotoCredits}
          />
        )}
      </div>
    </div>
    </div>
  )
}

/**
 * /book/:pid and /album/:pid — hero, stats, purchase, trailer/preview,
 * description with credits, properties, more by author, reviews, see also.
 */
export default function ProductPage({ pid, sub, product, ownershipKnown = false }) {
  const t = useT()
  const { auth } = useClientAuth()
  const contentTypeLabel = useProfileName(product?.profile)
  const { isFavorite, toggle: toggleFavoriteState } = useFavorite(product)
  const { account, resolved } = useStoreAccount()
  const [registerDialog, setRegisterDialog] = useState(false)


  const { avgRating, numRatings } = analyzeRatings(product.ratings)
  const physical = isPhysicalProduct(product)
  const isAlbum = product.profile?.page === "album"
  const owned = product.is_bought || ownsProduct(account, product.pid) || (product.free_access && auth)

  const reviewsRef = useRef(null)
  const scrollToReviews = () => reviewsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  // "Rate this product" opens the review dialog at once (the leave-review sub-route); the page stays where it is
  const openReview = () => navigate(`${buildContentUrl(product)}/leave-review`, { keepScrollPosition: true })
  const toggleFavourite = () => {
    if (!auth) return requestSignIn()
    toggleFavoriteState()
  }
  // Free titles open in the player right away; guests are invited to register
  // so the title is kept in their library (the player still plays it).
  const onFreeGet = () => {
    openPlayer(product.pid)
    if (!auth) setRegisterDialog(true)
  }

  // Whether this reader owns the title decides what stands here, and the
  // answer arrives with the browser's remembered account rather than from the
  // server — which is why the page used to render "Buy" and then correct
  // itself. Until it is known, the space is simply held: the same size, no
  // button, nothing to see change.
  // Two things can say this reader owns the title: the product as the browser
  // itself asked for it (`is_bought`), and the remembered store account. The
  // account can only grant ownership early, never deny it — a title bought on
  // this device may not be in the account's list, and "not in the list" is
  // not the same as "not owned".
  const settled = ownershipKnown || (resolved && ownsProduct(account, product.pid))
  const purchaseInner = !settled ? (
    <div aria-hidden="true" data-testid="purchase-pending" className="w-[70px] md:w-[80px] lg:w-[100px] h-[70px] md:h-[80px] lg:h-[100px]" />
  ) : owned ? (
    <button type="button" aria-label={t`Listen`} data-testid="listen-button" className="text-white hover:scale-105 transition-transform" onClick={() => openPlayer(product.pid)}>
      <PlayCircleIcon className="w-[70px] md:w-[80px] lg:w-[100px] h-auto" />
      <span className="sr-only">{t`Listen`}</span>
    </button>
  ) : (
    <PurchaseButtons product={product} account={auth} onFreeGet={onFreeGet} />
  )

  // the discount is for a buyer: none on a free title or one already had
  const heroDiscount = settled && !owned && !product.free_access ? activeDiscountPercent(product) : 0

  const purchase = (
    <div data-testid="purchase-area" data-state={!settled ? "pending" : owned ? "owned" : "buy"}>
      {purchaseInner}
    </div>
  )

  return (
    <div className="w-full">
      {/* Hero */}
      <div className="relative isolate w-full md:h-[500px] overflow-hidden">
        <div className="hidden md:block">
          <PanningCover image={product.cover} alt={product.name} />
        </div>
        <div className="md:hidden absolute top-0 left-0 w-full z-[1]">
          <Img src={product.cover} alt={product.name} loading="eager" fetchPriority="high" sizes="100vw" className="w-full h-[300px] sm:h-[400px]" />
          {/* on a phone the trailer sits over the picture, not under the stats */}
          {!physical && <TrailerButton product={product} className="absolute top-4 right-4 min-w-[120px] rounded-full bg-dark" />}
        </div>
        <div
          className={`w-full min-h-[320px] sm:min-h-[400px] md:min-h-[500px] flex flex-col items-start justify-end bg-dark md:bg-transparent md:bg-gradient-to-t md:from-black/70 md:to-transparent ${HERO_PX} pt-[320px] sm:pt-[420px] md:pt-0 ${physical ? "pb-5 md:pb-[90px]" : "pb-0 md:pb-[140px]"}`}
        >
          <div className="flex flex-row items-start md:items-center gap-4 sm:gap-5 md:gap-8">
            <div className="relative shrink-0">
              <Img src={product.square} alt={product.name} width={240} height={240} loading="eager" className="w-[73px] h-[73px] md:w-[240px] md:h-[240px] rounded-[1px] border border-white/30 overflow-hidden" />
              {/* the discount on the cover; the price before it is struck in the buy button */}
              {heroDiscount > 0 && (
                <span className="absolute top-1 -right-2 md:top-3 md:right-0 md:translate-x-1/2 inline-flex h-[18px] md:h-[34px] items-center rounded-full bg-primary px-1.5 md:px-4 text-[10px] md:text-[16px] font-bold text-white shadow-md" data-testid="book-discount">
                  -{heroDiscount}%
                </span>
              )}
            </div>
            <div className="flex flex-col items-start justify-center gap-1.5 max-w-[350px] sm:max-w-[600px] md:max-w-[830px]">
              <h1 className="m-0 text-white text-left font-bold leading-tight text-[16px] sm:text-[24px] md:text-[34px] lg:text-[42px] xl:text-[50px]">{product.name}</h1>
              {/* the type: a chip to its own page (/type/<id>) */}
              {contentTypeLabel && <Chip tone="outline" size="sm" label={contentTypeLabel} to={`/type/${product.profile?.id}`} className="uppercase tracking-wide text-[11px] border-white text-white hover:bg-white hover:text-dark" data-testid="product-type-title" />}
              {physical && (
                <span className="inline-flex items-center gap-1 text-white text-[12px] md:text-[14px]">
                  {avgRating.toFixed(1)}
                  <StarIcon className="w-4 h-4 text-white" />
                  {formatNumber(numRatings)} {t`Reviews`}
                </span>
              )}
            </div>
          </div>
          {physical && (
            <div className="w-full mt-5 md:mt-6 flex flex-col items-center md:items-start gap-4">
              {purchase}
              {physical && product.free_shipping && <Chip tone="success" label={t`Free Shipping`} />}
            </div>
          )}
        </div>
      </div>

      {/* Stats panel */}
      {!physical && (
        <div className="w-full bg-dark">
        <div className={`relative w-full flex flex-col md:flex-row items-center justify-center gap-6 md:gap-9 px-4 sm:px-8 pt-6 md:pt-[30px] pb-5 md:pb-[30px]`}>
          <div className="flex flex-row items-center md:items-end justify-center gap-2.5 sm:gap-5 h-[46px] text-white text-[14px]">
            {/* the rating as a physical product's card shows it (stars, the breakdown on hover), and the way to add one */}
            <div className="flex flex-col items-start justify-end gap-1">
              {numRatings > 0 && <ProductRatingSummary product={product} variant="hero" onClickReviews={scrollToReviews} />}
              <button type="button" onClick={openReview} data-testid="rate-product" className="inline-flex items-center gap-1.5 font-medium hover:text-primary">
                <StarOutline className="w-5 h-5" />
                {t`Rate this product`}
              </button>
            </div>
            <span className="w-px h-full bg-white/20" />
            <div className="flex flex-col items-center">
              <span>{formatNumber(product.views)}</span>
              <span>{t`Views`}</span>
            </div>
            <span className="w-px h-full bg-white/20" />
            <button type="button" onClick={toggleFavourite} className="flex flex-col items-center" aria-label={isFavorite ? t`Added to favorites` : t`Add to favorites`} aria-pressed={isFavorite} data-testid="favorite-button">
              {isFavorite ? <HeartSolid className="w-6 h-6 text-primary" /> : <HeartIcon className="w-6 h-6" />}
              <span>{isFavorite ? t`Added to favorites` : t`Add to favorites`}</span>
            </button>
          </div>

          <div className="flex flex-col items-center gap-3">
            {purchase}
            {physical && product.free_shipping && <Chip tone="success" label={t`Free Shipping`} />}
          </div>

          <div className={`${isAlbum ? "flex" : "hidden md:flex"} flex-row items-center gap-4 md:w-[363px] mt-6 md:mt-0`}>
            <TrailerButton product={product} className="hidden md:inline-flex" />
            {isAlbum && <PlaylistPreviewButton product={product} />}
          </div>
        </div>
        {/* a digital title: read or played here, never handed over as a file */}
        <div className="flex flex-row items-start justify-center gap-2 px-4 sm:px-8 pb-6 md:pb-[30px]" data-testid="digital-disclaimer">
          <InformationCircleIcon className="w-5 h-5 shrink-0 text-neutral_1" aria-hidden="true" />
          <Typography variant="txt1" className="block text-neutral_2 mt-0.5">
            {t`This title is read or played here on Aldoo: it is not a file to download.`}
          </Typography>
        </div>
        </div>
      )}

      {sub[0] === "leave-review" && sub[1] !== "success" && <LeaveReview product={product} rootPath={buildContentUrl(product)} />}
      {sub[0] === "leave-review" && sub[1] === "success" && <ReviewSuccessfullyAdded rootPath={buildContentUrl(product)} />}

      {/* Breadcrumb */}
      <div className={`w-full ${PAGE_PX} pt-4 md:pt-6`}>
        <ProductBreadcrumb product={product} itemName={product.name} />
      </div>

      {/* Description */}
      <div className={`w-full max-w-w1_xl mx-auto ${PAGE_PX} pt-[80px] pb-[60px] flex flex-col`}>
        <div className="flex flex-row items-baseline justify-between h-[50px] mb-4 md:mb-6">
          <h2 className="m-0 text-[24px] md:text-[28px] font-bold leading-tight">{t`Description`}</h2>
          {contentTypeLabel && <Chip tone="dark" size="sm" className="md:h-[28px] md:text-[13px]" to={`/type/${product.profile.id}`} label={contentTypeLabel} />}
        </div>
        <div className="flex flex-row justify-between gap-5 lg:gap-10">
          <Img src={product.square} alt={product.name} width={300} height={300} className="hidden md:block w-[150px] h-[150px] lg:w-[300px] lg:h-[300px] shrink-0" />
          <ProductDescription product={product} />
        </div>
        <ProductProperties product={product} />
        <AboutAuthor product={product} />
        <MoreByAuthor product={product} />
        {/* what else they may like, then the reviews */}
        <div className="mt-[60px]">
          <SeeAlsoHorizontal list={product.see_also} />
        </div>
        <div ref={reviewsRef} className="mt-[60px]">
          <ProductReviews product={product} />
        </div>
        <RecentlyViewed except={product.pid} className="mt-16" />
      </div>

      {registerDialog && (
        <div className="fixed inset-0 z-[550] bg-black/60 flex items-center justify-center p-4" onClick={() => setRegisterDialog(false)}>
          <div className="w-full max-w-[480px] md:max-w-[820px] rounded-[24px] bg-paper overflow-hidden flex flex-col md:flex-row" onClick={(e) => e.stopPropagation()}>
            <img src="/static/images/subscribe_dialog.png" alt="" className="hidden md:block w-[340px] object-cover" />
            <div className="flex-1 p-8 flex flex-col items-center gap-6 text-center">
            <h2 className="m-0 text-[18px] font-bold">{t`Get free content every day`}</h2>
            <div className="flex flex-col">
              <span className="text-[20px] font-black text-primary">{t`Create your account`}</span>
              <span className="text-[26px] font-black text-primary">{t`free of charge`}</span>
            </div>
            <Button variant="primary_l" className="w-[250px] min-w-0" onClick={() => requestSignIn()}>
              {t`Proceed`}
            </Button>
            <button type="button" className="text-text_secondary hover:underline" onClick={() => setRegisterDialog(false)}>
              {t`Cancel`}
            </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
